import Swal from "sweetalert2"
import { ref, computed } from "vue"

// Límites de Google Flights (verificados en su selector de pasajeros)
export const MAX_PASAJEROS = 9
export const MAX_INFANTES_POR_ADULTO = 2

// Cómo se cotiza el vuelo: niños de 12 a 17 con tarifa de adulto e infantes
// con asiento (mismo cálculo que core/pasajeros.js del backend)
export function pasajerosParaVuelo(habitaciones) {
  let adultos = 0, ninos = 0, infantes = 0

  ;(habitaciones || []).forEach(h => {
    const mayores = (h.edadesNinos || []).filter(e => Number(e) >= 12).length
    adultos += Number(h.adultos || 0) + mayores
    ninos += Number(h.ninos || 0) - mayores
    infantes += Number(h.infantes || 0)
  })

  return { adultos, ninos, infantes }
}

/** Motivo por el que Google Flights no aceptaría el grupo, o null */
export function restriccionGoogleFlightsDe(habitaciones) {
  const { adultos, ninos, infantes } = pasajerosParaVuelo(habitaciones)

  if (adultos < 1) {
    return "Debe viajar al menos 1 adulto (los niños de 12 a 17 años cuentan como adulto)."
  }

  if (adultos + ninos + infantes > MAX_PASAJEROS) {
    return `Máximo ${MAX_PASAJEROS} pasajeros en total, sumando todas las habitaciones.`
  }

  if (infantes > adultos * MAX_INFANTES_POR_ADULTO) {
    return `Máximo ${MAX_INFANTES_POR_ADULTO} infantes por adulto: hay ${infantes} infantes y ${adultos} adulto(s) (los niños de 12 a 17 años cuentan como adulto).`
  }

  return null
}

export function useHabitaciones() {

  const habitaciones = ref([
    {
      adultos: 2,
      ninos: 0,
      infantes: 0,
      edadesNinos: [],
      edadesInfantes: []
    }
  ])

  const maxHabitaciones = 4

  // =========================
  // SYNC EDADES
  // =========================
  function syncEdades() {
    habitaciones.value.forEach(h => {

      while (h.edadesNinos.length < h.ninos) {
        h.edadesNinos.push(5)
      }
      while (h.edadesNinos.length > h.ninos) {
        h.edadesNinos.pop()
      }

      while (h.edadesInfantes.length < h.infantes) {
        h.edadesInfantes.push(1)
      }
      while (h.edadesInfantes.length > h.infantes) {
        h.edadesInfantes.pop()
      }

    })
  }

  // =========================
  // SETTERS
  // =========================
  function setAdultos(index, value) {
    habitaciones.value[index].adultos = Number(value)
  }

  function setNinos(index, value) {
    habitaciones.value[index].ninos = Number(value)
    syncEdades()
  }

  function setInfantes(index, value) {
    habitaciones.value[index].infantes = Number(value)
    syncEdades()
  }

  // =========================
  // CRUD
  // =========================
  function agregarHabitacion() {
    if (habitaciones.value.length >= maxHabitaciones) return

    habitaciones.value.push({
      adultos: 2,
      ninos: 0,
      infantes: 0,
      edadesNinos: [],
      edadesInfantes: []
    })
  }

  function eliminarHabitacion() {
    if (habitaciones.value.length <= 1) return
    habitaciones.value.pop()
  }

  // =========================
  // VALIDACIONES
  // =========================
  function validarPasajeros() {
    const total = habitaciones.value.reduce((acc, h) =>
      acc + h.adultos + h.ninos + h.infantes, 0)

    if (total > 9) {
      Swal.fire({
        icon: "warning",
        title: "Máximo 9 pasajeros"
      })
      return false
    }

    // Niño = de 2 a 17 años (el input ya lo corrige; esto cubre sesiones
    // restauradas o un valor que no alcanzó a pasar por @change)
    const ninoInvalido = habitaciones.value.some(h =>
      (h.edadesNinos || []).some(e => !(Number(e) >= 2 && Number(e) <= 17)))

    if (ninoInvalido) {
      Swal.fire({
        icon: "warning",
        title: "Edad de niño inválida",
        text: "Los niños deben tener entre 2 y 17 años. Menores de 2 van como infantes y de 18 en adelante como adultos."
      })
      return false
    }

    // Infante = hasta 1 año (el input ya lo limita; esto cubre sesiones
    // restauradas que traigan una edad mayor)
    const infanteInvalido = habitaciones.value.some(h =>
      (h.edadesInfantes || []).some(e => Number(e) < 0 || Number(e) > 1))

    if (infanteInvalido) {
      Swal.fire({
        icon: "warning",
        title: "Edad de infante inválida",
        text: "Los infantes solo pueden tener hasta 1 año. Si es mayor, regístralo como niño."
      })
      return false
    }

    // Restricciones de Google Flights para todo el grupo (los vuelos se
    // cotizan juntos): mismo cálculo que core/pasajeros.js del backend
    const restriccion = restriccionGoogleFlights()

    if (restriccion) {
      Swal.fire({
        icon: "warning",
        title: "Restricción de Google Flights",
        text: restriccion
      })
      return false
    }

    return true
  }

  function restriccionGoogleFlights() {
    return restriccionGoogleFlightsDe(habitaciones.value)
  }

  // =========================
  // TOTALES
  // =========================
  function totales() {
    let adultos = 0, ninos = 0, infantes = 0
    let edadesNinos = [], edadesInfantes = []

    habitaciones.value.forEach(h => {
      adultos += h.adultos
      ninos += h.ninos
      infantes += h.infantes
      edadesNinos.push(...h.edadesNinos)
      edadesInfantes.push(...h.edadesInfantes)
    })

    return { adultos, ninos, infantes, edadesNinos, edadesInfantes }
  }
  const totalPersonas = computed(() => {

    return habitaciones.value.reduce((acc, h) => {

      return acc +
        Number(h.adultos) +
        Number(h.ninos) +
        Number(h.infantes)

    }, 0)

  })
  return {
    habitaciones,
    agregarHabitacion,
    eliminarHabitacion,
    validarPasajeros,
    restriccionGoogleFlights,
    totales,
    setAdultos,
    setNinos,
    setInfantes,
    totalPersonas
  }
}